import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContratsFormateurs } from '../services/formateurService';
import { AvatarFormateur, codeFormateur } from './Formateurs';
import ContratFormateurActions from '../components/ContratFormateurActions';
import { formatDate } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';

/**
 * Menu Formateurs > Contrats des formateurs : tous les contrats où un
 * formateur intervient, groupés par formateur — pour voir d'un coup d'œil
 * qui anime quoi, chez qui, et quand.
 *
 * Une journée sans fiche (nom posé en texte libre à la planification) figure
 * aussi : elle concerne bien un intervenant, même sans fiche à ouvrir.
 */
function FormateursContrats() {
  const navigate = useNavigate();
  const [seances, setSeances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saisie, setSaisie] = useState('');
  // Change quand un contrat vient d'être produit : la liste se relit.
  const [rechargement, setRechargement] = useState(0);

  useEffect(() => {
    let vivant = true;
    getContratsFormateurs()
      .then((res) => vivant && setSeances(res.data || []))
      .catch((err) => vivant && setError(err.message || 'Chargement impossible.'))
      .finally(() => vivant && setLoading(false));
    return () => {
      vivant = false;
    };
  }, [rechargement]);

  /** Sans accents ni majuscules, pour que la recherche pardonne la saisie. */
  const cle = (texte) =>
    String(texte || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();

  const groupes = useMemo(() => {
    const mots = cle(saisie).split(/\s+/).filter(Boolean);
    const filtrees = mots.length
      ? seances.filter((s) => {
          const texte = cle(
            [s.formateurNom, s.societeNom, s.contratNumero, s.intitule].join(' ')
          );
          return mots.every((mot) => texte.includes(mot));
        })
      : seances;

    // Un groupe par formateur : la fiche quand elle existe, le nom sinon.
    const parFormateur = new Map();
    for (const s of filtrees) {
      const id = s.formateurId ? `fiche-${s.formateurId}` : `libre-${s.formateurNom}`;
      if (!parFormateur.has(id)) {
        parFormateur.set(id, {
          id,
          formateurId: s.formateurId,
          nom: s.formateurNom || '—',
          prenom: s.formateurPrenom,
          nomFamille: s.formateurNomFamille,
          email: s.formateurEmail,
          seances: [],
        });
      }
      parFormateur.get(id).seances.push(s);
    }
    return [...parFormateur.values()].sort((a, b) =>
      String(a.nom).localeCompare(String(b.nom), 'fr')
    );
  }, [seances, saisie]);

  const nbSeances = groupes.reduce((n, g) => n + g.seances.length, 0);

  return (
    <div className="devis-page devis-page-scroll">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Contrats des formateurs</h1>
          <p className="devis-subtitle">
            Les contrats où chaque formateur intervient, avec leurs journées.
          </p>
        </div>
      </div>

      <div className="devis-toolbar">
        <input
          type="search"
          className="devis-search"
          placeholder="Rechercher un formateur, un client, un contrat..."
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
        {!loading && !error && (
          <span className="devis-status-detail">
            {groupes.length} formateur{groupes.length > 1 ? 's' : ''} ·{' '}
            {nbSeances} journée{nbSeances > 1 ? 's' : ''} planifiée{nbSeances > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading && <div className="devis-state">Chargement...</div>}
      {!loading && error && <div className="devis-state devis-state-error">{error}</div>}
      {!loading && !error && groupes.length === 0 && (
        <div className="devis-card clients-vide">
          {saisie.trim()
            ? 'Aucun contrat ne correspond à cette recherche.'
            : 'Aucun formateur n’est encore assigné à une journée planifiée.'}
        </div>
      )}

      {groupes.map((g) => (
        /* Un formateur = une carte, avec son en-tête et ses journées en
           lignes fines : les groupes se distinguent d'un coup d'œil. */
        <section className="fg-carte" key={g.id}>
          <header className="fg-tete">
            <AvatarFormateur
              formateur={{ prenom: g.prenom, nom: g.nomFamille || g.nom, email: g.email }}
            />
            <div className="fg-tete-texte">
              <div className="fg-nom">
                {g.formateurId && (
                  <span className="formateur-code">
                    {codeFormateur({ prenom: g.prenom, nom: g.nomFamille, id: g.formateurId })}
                  </span>
                )}
                {g.nom}
              </div>
              {g.email && <div className="fg-email">{g.email}</div>}
            </div>
            <span className="devis-status-pill">
              {g.seances.length} journée{g.seances.length > 1 ? 's' : ''}
            </span>
            {g.formateurId && (
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => navigate(`/formateurs/${g.formateurId}`)}
              >
                Fiche
              </button>
            )}
          </header>

          <div className="fg-liste">
            <div className="fg-ligne fg-ligne-contrat fg-entete" aria-hidden="true">
              <div>CONTRAT · FORMATION</div>
              <div>CLIENT</div>
              <div>DATES</div>
              <div>LIEU</div>
              <div>CONTRAT DU FORMATEUR</div>
              <div />
            </div>

            {g.seances.map((s) => (
              <div className="fg-ligne fg-ligne-contrat" key={s.id}>
                {/* Contrat et formation : une seule colonne, le numéro en
                    pastille au-dessus de l'intitulé. */}
                <div className="fg-cellule" data-label="Contrat et formation">
                  <span className="fg-contrat" title={`Contrat ${s.contratNumero}`}>
                    {s.contratNumero}
                  </span>
                  <span className="fg-texte fg-formation" title={s.intitule || ''}>
                    {s.intitule || '—'}
                  </span>
                  {s.groupeLibelle && <span className="fg-sous">{s.groupeLibelle}</span>}
                </div>
                <div className="fg-cellule" data-label="Client">
                  <span className="fg-texte" title={s.societeNom || ''}>
                    {s.societeNom || '—'}
                  </span>
                </div>
                <div className="fg-cellule" data-label="Dates">
                  <span className="fg-texte">
                    {(s.jours || []).map((j) => formatDate(j)).join(', ') || '—'}
                  </span>
                </div>
                <div className="fg-cellule" data-label="Lieu">
                  <span className="fg-texte" title={s.lieu || ''}>
                    {s.modalite === 'distanciel' ? 'À distance' : s.lieu || '—'}
                  </span>
                </div>
                <div className="fg-cellule" data-label="Contrat du formateur">
                  <ContratFormateurActions
                    sessionId={s.id}
                    document={s.contratFormateurPret ? { id: s.contratFormateurId, le: s.contratFormateurLe } : null}
                    // Sans fiche, rien à produire : le contrat s'adresse à une fiche.
                    peutGenerer={Boolean(s.formateurId)}
                    titre={`Contrat formateur — ${s.formateurNom || ''}`.trim()}
                    sousTitre={[s.contratNumero, s.societeNom, s.intitule].filter(Boolean).join(' · ')}
                    onChange={() => setRechargement((n) => n + 1)}
                  />
                </div>
                <div className="fg-cellule fg-actions">
                  <button
                    type="button"
                    className="devis-btn devis-btn-sm"
                    onClick={() => navigate(`/calendrier/session/formation/${s.id}`)}
                    title="Feuille de présence, participants et émargements de cette séance"
                  >
                    Détails
                  </button>
                  <button
                    type="button"
                    className="devis-btn devis-btn-sm"
                    onClick={() => navigate(`/clients/${s.contactId}`)}
                    title="Ouvrir le dossier du client"
                  >
                    Client
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default FormateursContrats;
